import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Avatar
} from "@mui/material";
import { CameraAlt as CameraAltIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { formatMoney } from '../../../utils/helpers';
import axios from "../../../api/axios";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import useData from "../../../Hooks/useData";
import styles from './UserCheckin.module.css';

// Get API base URL for serving images
const getApiBaseUrl = () => {
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://baptistmenapi.onrender.com';
  }
  return 'http://localhost:3001';
};

const UserCheckin = () => {
  const { eventId, userId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useData();
  const token = auth?.token;

  const [loading, setLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUpdatingPreferredName, setIsUpdatingPreferredName] = useState(false);
  const [isCompletingCheckin, setIsCompletingCheckin] = useState(false);

  const [attendeeData, setAttendeeData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [eventData, setEventData] = useState(null);

  // Certification type ID linked to this registration (if any)
  const [certificationTypeId, setCertificationTypeId] = useState(null);

  // Compute expiration = Dec 31 of (previous year + 3)
  const computeCertExpiration = () => {
    const now = new Date();
    const prevYear = (now.getMonth() === 11 && now.getDate() === 31)
      ? now.getFullYear()
      : now.getFullYear() - 1;
    return `${prevYear + 3}-12-31`;
  };

  const [currentImage, setCurrentImage] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [order, setOrder] = useState({});

  // Fetch attendee and user data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get(`/api/attendees/event/${eventId}/user/${userId}`);
      
      setAttendeeData(response.data.attendee);
      setUserData(response.data.user);
      setUserSettings(response.data.userSettings);
      setEventData(response.data.event);

      // Set current image from UserPicture or MiddleName
      if (response.data.userSettings?.UserPicture) {
        setCurrentImage(response.data.userSettings.UserPicture);
      } else if (response.data.user?.MiddleName) {
        setCurrentImage(response.data.user.MiddleName);
      }

      // Set preferred name
      setPreferredName(response.data.userSettings?.UserNickName || '');

      // Try to find the certification type linked to this attendee's registration
      const attendeeType = response.data.attendee?.BPAttendeeType;
      if (attendeeType) {
        try {
          const regsResp = await axiosPrivate.get(`/api/events/registrations/${eventId}`);
          const regs = Array.isArray(regsResp.data)
            ? regsResp.data
            : (Array.isArray(regsResp.data?.registrations) ? regsResp.data.registrations : []);
          const matched = regs.find(
            (r) => r.RegistrationName === attendeeType || r.SectionLabel === attendeeType
          );
          if (matched?.HasCertification && matched?.AssociatedCertification) {
            setCertificationTypeId(Number(matched.AssociatedCertification));
          }
        } catch (e) {
          // Non-critical: auto-certification skipped if registrations can't be fetched
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Failed to load attendee data', { variant: 'error' });
      navigate(`/admin/checkin/${eventId}`);
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, eventId, userId, enqueueSnackbar, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  //set order to use for balance instead of attendeebalance field
  // Replace the order useEffect
useEffect(() => {
  if (!attendeeData?.BPAttendeeOrderID) return;

  const fetchOrder = async () => {
    try {
      const res = await axiosPrivate.get(`/api/orders/${attendeeData.BPAttendeeOrderID}`);
      if (res.data) {
        setOrder(res.data);
      }
    } catch (ex) {
      console.error('Error fetching order:', ex);
    }
  };

  fetchOrder();
}, [attendeeData?.BPAttendeeOrderID, axiosPrivate]);

  // Build avatar source URL
  const buildAvatarSrc = (img) => {
    if (!img) return '';
    const base = String(img).startsWith('http') ? String(img) : `${getApiBaseUrl()}${img}`;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}t=${Date.now()}`;
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar('Image file size must be less than 5MB', { variant: 'error' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please select a valid image file', { variant: 'error' });
      return;
    }

    setIsUploadingImage(true);

    try {
      const data = new FormData();
      data.append('file', file);

      const response = await axios.post(`/api/users/${userId}/profile-picture`, data, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: true
      });

      if (response.data?.success && response.data?.url) {
        setCurrentImage(response.data.url);
        enqueueSnackbar('Photo uploaded successfully!', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to upload photo', { variant: 'error' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      enqueueSnackbar('Failed to upload photo: ' + (error.response?.data?.error || error.message), { variant: 'error' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Update preferred name
  const handleUpdatePreferredName = async () => {
    if (!userSettings?.UserSettingsID) {
      enqueueSnackbar('User settings not available', { variant: 'error' });
      return;
    }

    setIsUpdatingPreferredName(true);

    try {
      await axiosPrivate.put(`/api/users/usersettings/${userId}`, {
        UserNickName: preferredName
      });

      enqueueSnackbar('Preferred name updated successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error updating preferred name:', error);
      enqueueSnackbar('Failed to update preferred name', { variant: 'error' });
    } finally {
      setIsUpdatingPreferredName(false);
    }
  };

  // Complete check-in or checkout
  const handleCompleteCheckin = async () => {
    if (!attendeeData?.BPAttendeeID) {
      enqueueSnackbar('Attendee data not available', { variant: 'error' });
      return;
    }

    setIsCompletingCheckin(true);
    const isCheckedIn = !!attendeeData?.BPAttendeeCheckInDate;

    try {
      if (isCheckedIn) {
        // Checkout — backend will also revoke the auto-awarded cert
        const checkoutResp = await axiosPrivate.put(`/api/attendees/${attendeeData.BPAttendeeID}/checkout`);
        const msg = checkoutResp.data?.certificationRevoked
          ? 'Attendee checked out and certification removed.'
          : 'Attendee checked out successfully!';
        enqueueSnackbar(msg, { variant: 'success' });
      } else {
        // Check-in — backend auto-awards the cert if the registration has HasCertification set.
        // The frontend only creates it as a fallback when BPRegistrationID is missing.
        const checkinResp = await axiosPrivate.put(`/api/attendees/${attendeeData.BPAttendeeID}/checkin`);
        enqueueSnackbar('Check-in completed successfully!', { variant: 'success' });

        if (!checkinResp.data?.certificationAwarded && certificationTypeId && userData?.UserGUID) {
          const expiration = computeCertExpiration();
          try {
            await axiosPrivate.post(`/api/certification/certification-types/${certificationTypeId}/volunteers`, {
              userGuid: userData.UserGUID,
              expiration,
              notes: `Auto-certified on check-in for Event ${eventId} (attendeeId:${attendeeData.BPAttendeeID})`,
            });
          } catch (certError) {
            // 409/400 means the backend already created it — not an error
            if (certError?.response?.status !== 409 && certError?.response?.status !== 400) {
              console.error('Auto-certification fallback failed:', certError);
            }
          }
        }
      }

      // Navigate back to event detail page
      setTimeout(() => {
        navigate(`/admin/checkin/${eventId}`);
      }, 500);
    } catch (error) {
      console.error('Error updating check-in status:', error);
      enqueueSnackbar('Failed to update check-in status', { variant: 'error' });
      setIsCompletingCheckin(false);
    }
  };

  // Make payment
  const handleMakePayment = () => {
    navigate(`/makepayment/${eventId}/${userId}`);
  };

  // Cancel and go back
  const handleCancel = () => {
    navigate(`/admin/checkin/${eventId}`);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  const fullName = `${attendeeData?.BPAttendeeFN || ''} ${attendeeData?.BPAttendeeLN || ''}`.trim() || 'N/A';
  const backgroundCheckDate = formatDate(attendeeData?.BPAttendeeBackGroundCheckDate);
  const volunteerNumber = userData?.UserID || attendeeData?.BPAttendeeUserID || 'N/A';
  const registrationType = attendeeData?.BPAttendeeType || 'General';
  const balanceDue = order?.Balance != null ? Number(order.Balance) : 0;
  const isAlreadyCheckedIn = !!attendeeData?.BPAttendeeCheckInDate;

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Event Check-In</h1>
              <p>Check attendees in as they arrive and track attendance in real time.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white mt-5">
        <Box>
          <Typography variant="h5" className={styles.eventTitle}>
            Check-In for {fullName}
          </Typography>

          <Typography variant="body1" className={styles.summaryText}>
            Attendee Information
          </Typography>

          <Paper className={styles.contentPaper}>
        <Box className={styles.contentContainer}>
          {/* Photo Section */}
          <Box className={styles.photoSection}>
            <Box className={styles.photoPlaceholder}>
              {currentImage ? (
                <Avatar
                  src={buildAvatarSrc(currentImage)}
                  className={styles.photoAvatar}
                  alt="Attendee Photo"
                />
              ) : (
                <CameraAltIcon className={styles.cameraIcon} />
              )}
            </Box>
            
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              id="photo-upload-input"
              disabled={isUploadingImage}
            />
            <label htmlFor="photo-upload-input" style={{ textAlign: 'center' }}>
              <Button
                component="span"
                className={styles.takePhotoButton}
                disabled={isUploadingImage}
                fullWidth
                startIcon={!isUploadingImage && <CameraAltIcon />}
              >
                {isUploadingImage ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'TAKE/SAVE PHOTO'
                )}
              </Button>
            </label>
          </Box>

          {/* Info Section */}
          <Box className={styles.infoSection}>
            <Box className={styles.infoField}>
              <Typography variant="body2" className={styles.fieldLabel}>Attendee Name</Typography>
              <Typography variant="body1" className={styles.fieldValue}>{fullName}</Typography>
            </Box>

            <Box className={styles.infoField}>
              <Typography variant="body2" className={styles.fieldLabel}>Background Check</Typography>
              <Typography variant="body1" className={styles.fieldValue}>{backgroundCheckDate}</Typography>
            </Box>

            <Box className={styles.infoField}>
              <Typography variant="body2" className={styles.fieldLabel}>Volunteer Number</Typography>
              <Typography variant="body1" className={styles.fieldValue}>{volunteerNumber}</Typography>
            </Box>

            <Box className={styles.infoField}>
              <Typography variant="body2" className={styles.fieldLabel}>Registration</Typography>
              <Typography variant="body1" className={styles.fieldValue}>{registrationType}</Typography>
            </Box>

            <Box className={styles.infoField}>
              <Typography variant="body2" className={styles.fieldLabel}>Balance Due</Typography>
              <Typography variant="body1" className={styles.fieldValue}>{formatMoney(balanceDue)}</Typography>
            </Box>

            <Box className={styles.infoField}>
              <Typography variant="body2" className={styles.fieldLabel}>Preferred Name</Typography>
              <Box className={styles.preferredNameRow}>
                <TextField
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                  variant="outlined"
                  size="small"
                  className={styles.preferredNameInput}
                  disabled={isUpdatingPreferredName}
                />
                <Button
                  className={styles.updateButton}
                  onClick={handleUpdatePreferredName}
                  disabled={isUpdatingPreferredName}
                >
                  {isUpdatingPreferredName ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    'UPDATE'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box className={styles.actionButtons}>
          <Button
            className={`${styles.actionButton} ${isAlreadyCheckedIn ? styles.checkoutButton : styles.completeButton}`}
            onClick={handleCompleteCheckin}
            disabled={isCompletingCheckin}
          >
            {isCompletingCheckin ? (
              <CircularProgress size={20} color="inherit" />
            ) : isAlreadyCheckedIn ? (
              'CHECKOUT'
            ) : (
              'COMPLETE CHECK-IN'
            )}
          </Button>
          <Button
            className={`${styles.actionButton} ${styles.paymentButton}`}
            onClick={handleMakePayment}
          >
            MAKE PAYMENT
          </Button>
          <Button
            className={`${styles.actionButton} ${styles.cancelButton}`}
            onClick={handleCancel}
          >
            CANCEL
          </Button>
        </Box>
      </Paper>
        </Box>
      </div>
    </main>
  );
};

export default UserCheckin;
